import Grid from '../../../Geodata/grid';
import { TriangleVertices } from '@data/geometry/Functions';
import FVector from '@data/geometry/FVector';
import type { Material, RawDataGroup, RawDataPoint, RawTriangle } from '@visualizer/types';
import { PreProcessingTypes } from '@visualizer/types';
import type { SensorPoint } from '../../../Simulations/SensorPoint';
import * as THREE from 'three';
import {
    buildBuildingTriangleSpatialTree,
    computeClosestBuildingDistance,
    computeClosestBuildingPerpendicularDistance,
} from './BuildingTriangleSpatialTree';

type PointLike = FVector | RawDataPoint;

type SensorPointLike = Pick<SensorPoint, 'getID' | 'setDistanceToProjectSketch'> & {
    getCenter(): FVector | undefined;
};

const PROJECT_SKETCH_MAX_DISTANCE = 50;

export function createDefaultRoofMaterial(): Material {
    return {
        stroke: new THREE.LineBasicMaterial({
            color: 0,
            linewidth: 1,
            depthWrite: false,

            // avoid flickering
            // depthTest: false,
            // depthWrite: false,
        }),
        material: new THREE.MeshBasicMaterial({
            color: 0,
            side: THREE.DoubleSide,
            polygonOffset: true,
            depthTest: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        }),
        shadowMaterial: new THREE.MeshLambertMaterial({
            color: 0,
            side: THREE.DoubleSide,
            flatShading: true,
            polygonOffset: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        }),
    };
}

export function createDefaultWallMaterial(): Material {
    return {
        stroke: new THREE.LineBasicMaterial({
            color: 0,
            linewidth: 1,
            depthWrite: false,
        }),
        material: new THREE.MeshBasicMaterial({
            color: 0,
            side: THREE.DoubleSide,
            polygonOffset: true,
            depthTest: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        }),
        shadowMaterial: new THREE.MeshLambertMaterial({
            color: 0,
            side: THREE.DoubleSide,
            flatShading: true,
            polygonOffset: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        }),
    };
}

export function createDefaultFloorMaterial(): Material {
    return {
        stroke: new THREE.LineBasicMaterial({
            color: 0,
            linewidth: 1,
            depthWrite: false,
        }),
        material: new THREE.MeshBasicMaterial({
            color: 0,
            side: THREE.DoubleSide,
            polygonOffset: true,
            depthTest: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        }),
        shadowMaterial: new THREE.MeshLambertMaterial({
            color: 0,
            side: THREE.DoubleSide,
            flatShading: true,
            polygonOffset: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        }),
    };
}

function toFVector(point: PointLike | undefined): FVector | undefined {
    if (!point) {
        return undefined;
    }

    if (point instanceof FVector) {
        return point;
    }

    return new FVector(point.x, point.y, point.z);
}

function buildTriangle(points: PointLike[], triangle: RawTriangle): TriangleVertices | undefined {
    const vectorA = toFVector(points[triangle.A]);
    const vectorB = toFVector(points[triangle.B]);
    const vectorC = toFVector(points[triangle.C]);

    if (!vectorA || !vectorB || !vectorC) {
        return undefined;
    }

    return { A: vectorA, B: vectorB, C: vectorC };
}

function getSensorPoints(): SensorPointLike[] {
    return Grid.notNullSensorPoints;
}

export function extractTriangles(data: RawDataGroup[], allowedTypes?: Set<PreProcessingTypes>): TriangleVertices[] {
    const triangles: TriangleVertices[] = [];

    for (const group of data) {
        if (allowedTypes && !allowedTypes.has(group.type)) {
            continue;
        }
        const objects = group.objects ?? [];
        for (const object of objects) {
            const points = (object.points ?? []) as PointLike[];
            const objectTriangles = (object.triangles ?? []) as RawTriangle[];

            for (const triangle of objectTriangles) {
                const parsedTriangle = buildTriangle(points, triangle);
                if (parsedTriangle) {
                    triangles.push(parsedTriangle);
                }
            }
        }
    }

    return triangles;
}

export function extractTriangleGroups(data: RawDataGroup[], allowedTypes?: Set<PreProcessingTypes>): TriangleVertices[][] {
    const triangleGroups: TriangleVertices[][] = [];

    for (const group of data) {
        if (allowedTypes && !allowedTypes.has(group.type)) {
            continue;
        }

        for (const object of group.objects ?? []) {
            const points = (object.points ?? []) as PointLike[];
            const objectTriangles = (object.triangles ?? []) as RawTriangle[];
            const triangles: TriangleVertices[] = [];

            for (const triangle of objectTriangles) {
                const parsedTriangle = buildTriangle(points, triangle);
                if (parsedTriangle) {
                    triangles.push(parsedTriangle);
                }
            }

            if (triangles.length > 0) {
                triangleGroups.push(triangles);
            }
        }
    }

    return triangleGroups;
}

export function updateProjectSketchSensorDistances(data: RawDataGroup[]): void {
    console.time('Benchmark close to project calculation');
    const roofTriangleGroups = extractTriangleGroups(data, new Set([PreProcessingTypes.PROJECT_SKETCH_ROOF]));
    const wallTriangleGroups = extractTriangleGroups(data, new Set([PreProcessingTypes.PROJECT_SKETCH_WALL]));
    if (roofTriangleGroups.length === 0 && wallTriangleGroups.length === 0) {
        return;
    }

    const sensorPoints = getSensorPoints();
    if (sensorPoints.length === 0) {
        return;
    }

    const roofTree = buildBuildingTriangleSpatialTree(roofTriangleGroups, PROJECT_SKETCH_MAX_DISTANCE);
    const wallTree = buildBuildingTriangleSpatialTree(wallTriangleGroups, PROJECT_SKETCH_MAX_DISTANCE);
    const overallTree = buildBuildingTriangleSpatialTree(wallTriangleGroups.concat(roofTriangleGroups), PROJECT_SKETCH_MAX_DISTANCE);

    for (const sensorPoint of sensorPoints) {
        if (sensorPoint.getID() === -1) {
            continue;
        }

        const sensorPosition = sensorPoint.getCenter();
        if (!sensorPosition) {
            continue;
        }

        sensorPoint.setDistanceToProjectSketch(
            computeClosestBuildingPerpendicularDistance(sensorPosition, wallTree),
            computeClosestBuildingPerpendicularDistance(sensorPosition, roofTree),
            computeClosestBuildingDistance(sensorPosition, overallTree)
        );
    }
    console.timeEnd('Benchmark close to project calculation');
}

// Used to get the Project sketches borders (no holes etc.) for Floor Based DesignScheme usage:
export function getProjectSketchBorders(projectSketchFloorGroups: RawDataGroup[], elevation: number): FVector[][] {
    const boundaryEdges = new Map<string, [number, number]>();
    const edgeCounts = new Map<string, number>();
    const points: Array<{ x: number; y: number }> = [];
    const pointIndexByKey = new Map<string, number>();

    const toPointIndex = (x: number, y: number) => {
        const key = `${x.toFixed(6)}|${y.toFixed(6)}`;
        const existing = pointIndexByKey.get(key);
        if (existing != null) return existing;
        const next = points.length;
        points.push({ x, y });
        pointIndexByKey.set(key, next);
        return next;
    };

    const addEdge = (a: number, b: number) => {
        if (a === b) return;
        const min = Math.min(a, b);
        const max = Math.max(a, b);
        const key = `${min}:${max}`;
        edgeCounts.set(key, (edgeCounts.get(key) ?? 0) + 1);
        if (!boundaryEdges.has(key)) boundaryEdges.set(key, [min, max]);
    };

    for (const group of projectSketchFloorGroups) {
        for (const object of group.objects ?? []) {
            const rawPoints = object.points ?? [];
            const rawTriangles = object.triangles ?? [];
            if (rawPoints.length < 3 || rawTriangles.length < 1) continue;

            for (const tri of rawTriangles) {
                const ia = tri.A;
                const ib = tri.B;
                const ic = tri.C;
                if (ia < 0 || ib < 0 || ic < 0 || ia >= rawPoints.length || ib >= rawPoints.length || ic >= rawPoints.length) continue;

                const a = rawPoints[ia];
                const b = rawPoints[ib];
                const c = rawPoints[ic];

                const pa = toPointIndex(a.x, a.y);
                const pb = toPointIndex(b.x, b.y);
                const pc = toPointIndex(c.x, c.y);

                addEdge(pa, pb);
                addEdge(pb, pc);
                addEdge(pc, pa);
            }
        }
    }

    const adjacency = new Map<number, number[]>();
    for (const [key, [u, v]] of boundaryEdges.entries()) {
        if ((edgeCounts.get(key) ?? 0) !== 1) continue;
        const listU = adjacency.get(u) ?? [];
        listU.push(v);
        adjacency.set(u, listU);
        const listV = adjacency.get(v) ?? [];
        listV.push(u);
        adjacency.set(v, listV);
    }
    if (!adjacency.size) return [];

    const visitedEdges = new Set<string>();
    const loops: number[][] = [];
    const markVisited = (a: number, b: number) => visitedEdges.add(`${Math.min(a, b)}:${Math.max(a, b)}`);
    const isVisited = (a: number, b: number) => visitedEdges.has(`${Math.min(a, b)}:${Math.max(a, b)}`);

    for (const [start] of adjacency.entries()) {
        const neighbors = adjacency.get(start) ?? [];
        for (const next of neighbors) {
            if (isVisited(start, next)) continue;
            const loop: number[] = [start];
            let prev = start;
            let curr = next;
            markVisited(prev, curr);

            while (true) {
                loop.push(curr);
                const currNeighbors = adjacency.get(curr) ?? [];
                const candidate = currNeighbors.find((n) => n !== prev && !isVisited(curr, n));
                if (candidate == null) {
                    if (currNeighbors.includes(start) && !isVisited(curr, start)) {
                        markVisited(curr, start);
                    }
                    break;
                }
                prev = curr;
                curr = candidate;
                markVisited(prev, curr);
                if (curr === start) break;
            }

            if (loop.length >= 3) loops.push(loop);
        }
    }

    if (!loops.length) return [];

    const signedArea = (loop: number[]) => {
        let area = 0;
        for (let i = 0; i < loop.length; i++) {
            const p = points[loop[i]];
            const q = points[loop[(i + 1) % loop.length]];
            area += p.x * q.y - q.x * p.y;
        }
        return area * 0.5;
    };

    const isPointInsideLoop = (x: number, y: number, loop: number[]) => {
        let inside = false;
        for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
            const pi = points[loop[i]];
            const pj = points[loop[j]];
            const intersects = pi.y > y !== pj.y > y && x < ((pj.x - pi.x) * (y - pi.y)) / (pj.y - pi.y) + pi.x;
            if (intersects) inside = !inside;
        }
        return inside;
    };

    const loopsWithArea = loops.map((loop) => ({ loop, area: Math.abs(signedArea(loop)) })).sort((a, b) => b.area - a.area);

    const exteriorLoops = loopsWithArea.filter(({ loop }, index) => {
        const firstPoint = points[loop[0]];
        for (let i = 0; i < index; i++) {
            if (isPointInsideLoop(firstPoint.x, firstPoint.y, loopsWithArea[i].loop)) return false;
        }
        return true;
    });

    return exteriorLoops.map(({ loop }) =>
        loop.map((idx) => {
            const point = points[idx];
            return new FVector(point.x, point.y, elevation);
        })
    );
}
